import React, { forwardRef, lazy, Suspense } from "react";
import ChartErrorBoundary from "./ChartErrorBoundary";
import { useChartData } from "./hooks/useChartData";
import { useChartResize } from "./hooks/useChartResize";
import { useChartLayout } from "./hooks/useChartLayout";
import { useChartLayoutSnapshot } from "./hooks/useChartLayoutSnapshot";
import { useChartInteractive } from "./hooks/useChartInteractive";
import { useChartManager } from "./hooks/useChartManager";
import ChartConfigProvider from "./components/ChartConfigProvider";
import ChartRenderer from "./components/ChartRenderer";
import { useChartsStore } from "../../store/chartStore";
import { useSettingsStore } from "../../store/settingsStore";
import { useThemeStore, getThemeColors } from "../../store/themeStore";
import { useOrderStore } from "../../store/orderStore.jsx";
import IndicatorContextMenu from "./components/IndicatorContextMenu";
import InteractiveContextMenu from "./components/InteractiveContextMenu";
import ChartContextMenu from "./components/ChartContextMenu";
import { isFeatureEnabled } from "../../data/Services/settings";
import PropTypes from "prop-types";
import { fitWidth } from "@chartLib/lib/helper";
import { useChartLocale } from "../../hooks/useChartLocale";
import { useModifierKeyTracking } from "../../hooks/useModifierKeyTracking";
import ReplayIndicator from "../../debug/ReplayIndicator";
import RenkoBrickSizeIndicator from "./components/RenkoBrickSizeIndicator";
import PointAndFigureBoxIndicator from "./components/PointAndFigureBoxIndicator";
import { AGGREGATION_TYPES } from "./utils/calculators";
import { isLastBarVisibleInExtents } from "./utils/chartHorizontalState.js";

// Feature flag: Debug panels - set VITE_DEBUG_PANELS=false to exclude from bundle
const DEBUG_PANELS_ENABLED = import.meta.env.VITE_DEBUG_PANELS === "true";

// Lazy load debug panels only when enabled - Vite will tree-shake when disabled
const DebugPanel = DEBUG_PANELS_ENABLED
  ? lazy(() => import("../../debug/DebugPanel"))
  : () => null;
const SeriesDebugPanel = DEBUG_PANELS_ENABLED
  ? lazy(() => import("../../debug/SeriesDebugPanel"))
  : () => null;

/**
 * Painel de aviso dos tipos de agregação (Renko, P&F…) quando a configuração
 * atual não gera gráfico. Antes do #432 esse caso caía no spinner (ou mantinha o
 * gráfico anterior na tela), então o usuário não tinha como saber o que ajustar
 * — daí o pill do próprio tipo vir junto, para corrigir sem sair daqui.
 */
const AggregationEmptyPanel = React.forwardRef(
  ({ chartType, chartId, chartData, periodicity }, ref) => (
    <div
      ref={ref}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "0.5rem",
        color: "#888",
        fontSize: "14px",
        position: "relative",
      }}
    >
      <span>
        Dados insuficientes para gerar o gráfico{" "}
        {chartType === "renko"
          ? "Renko"
          : chartType === "pointfigure"
            ? "P&F"
            : chartType}
      </span>
      <small style={{ color: "#666" }}>
        {chartType === "pointfigure"
          ? "Aumente o tamanho do box — o valor atual gera colunas demais para o histórico carregado"
          : "Tente ajustar o tamanho do tijolo ou carregar mais dados históricos"}
      </small>
      {chartType === "renko" && (
        <div style={{ marginTop: "1rem" }}>
          <RenkoBrickSizeIndicator
            chartId={chartId}
            chartData={chartData}
            periodicity={periodicity}
            inline={true}
          />
        </div>
      )}
      {chartType === "pointfigure" && (
        <div style={{ marginTop: "1rem" }}>
          <PointAndFigureBoxIndicator
            chartId={chartId}
            chartData={chartData}
            inline={true}
          />
        </div>
      )}
    </div>
  ),
);
AggregationEmptyPanel.displayName = "AggregationEmptyPanel";
AggregationEmptyPanel.propTypes = {
  chartType: PropTypes.string,
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chartData: PropTypes.array,
  periodicity: PropTypes.string,
};

const ChartWrapper = forwardRef((props, ref) => {
  const {
    chartId,
    width = 800,
    height = 300,
    type = "hybrid",
    ratio = 0.8,
    onInteractiveApiReady,
    isPointerInsideChartPanel = false,
  } = props;

  // Apply locale configuration globally
  useChartLocale();

  // Track modifier key releases for one-click trading
  useModifierKeyTracking();

  // Use the new chart manager hook
  const {
    resetKey,
    resetTriggered,
    setResetTriggered,
    periodicity,
    chartType,
    chartSymbol,
    indicators,
    barColorProvider,
    barColorConfig,
    renkoConfig,
    renkoConfigHash,
    pointFigureConfig,
    pointFigureConfigHash,
    initialData,
    dataVersion, // Version counter for triggering re-renders on data mutations
    pendingInvalidation,
    visibleBars,
    storedXExtents,
    sectionOrder,
    getDefaultSectionOrder,
    setSectionOrder,
    updateVisibleBars,
    handleChartCanvasVisibleBarsChange,
    handleChartCanvasXExtentsChange,
    handleLoadMore,
    moveSectionUp,
    moveSectionDown,
    handleReset,
    // Compare mode
    isCompareMode,
    comparisonSymbols,
    compareDataVersion, // Version counter for real-time comparison data updates
    addComparisonSymbol,
    removeComparisonSymbol,
    clearComparisons,
  } = useChartManager(chartId);

  // Subscribe to order changes to trigger re-renders when orders change
  // This ensures ChartRenderer re-renders when orders are updated
  // eslint-disable-next-line no-unused-vars
  const ordersBySymbol = useOrderStore((state) => state.ordersBySymbol);

  // Get global grid setting from settings store
  const showGrid = useSettingsStore((state) => state.showGrid);
  const theme = useThemeStore((state) => state.theme);
  const themeColors = getThemeColors(theme);

  // Use the new interactive hook
  const {
    indicatorMenuRef,
    interactiveMenuRef,
    chartMenuRef,
    mainInteractiveApi,
    handleMainInteractiveApiReady,
    handleInteractiveContextMenu,
    handleOpenEditModal,
    handleInteractiveEdit,
    handleInteractiveRemove,
  } = useChartInteractive();

  // Get the update and remove indicator functions from store
  const updateIndicatorConfig = useChartsStore(
    (state) => state.updateIndicatorConfig,
  );
  const removeIndicatorFromChart = useChartsStore(
    (state) => state.removeIndicatorFromChart,
  );
  const setChartHiddenState = useChartsStore(
    (state) => state.setChartHiddenState,
  );

  // Notify parent when main chart's interactive API is ready
  React.useEffect(() => {
    if (typeof onInteractiveApiReady === "function" && mainInteractiveApi) {
      onInteractiveApiReady(mainInteractiveApi);
    }
  }, [mainInteractiveApi, onInteractiveApiReady]);

  // Memoize the callback to prevent infinite re-render loop
  // Only recreate when chartId or updateVisibleBars changes, NOT when visibleBars changes
  // This prevents the infinite loop while allowing the chart to update properly
  const handleVisibleBarsChange = React.useCallback(
    (newVisibleBars) => {
      updateVisibleBars(chartId, newVisibleBars);
    },
    [chartId, updateVisibleBars],
  );

  // Stable callbacks for IndicatorContextMenu (prevents re-render on every tick)
  const handleIndicatorEditSave = React.useCallback(
    (cid, indicatorEntry, options, appearanceOptions) => {
      if (indicatorEntry && indicatorEntry.id) {
        updateIndicatorConfig(
          cid,
          indicatorEntry.id,
          options,
          appearanceOptions,
        );
      }
    },
    [updateIndicatorConfig],
  );

  const handleIndicatorRemove = React.useCallback(
    (cid, indicatorEntry) => {
      if (indicatorEntry && indicatorEntry.id) {
        removeIndicatorFromChart(cid, indicatorEntry.id);
      }
    },
    [removeIndicatorFromChart],
  );

  // Compute data + scales
  // Combine main dataVersion with compareDataVersion to trigger re-renders for both
  const combinedDataVersion = `${dataVersion}-${compareDataVersion}`;
  const chartState = useChartData({
    chartId,
    initialData,
    dataVersion: combinedDataVersion, // Combined version for main + comparison data updates
    chartType,
    indicators,
    barColorProvider,
    barColorConfig,
    renkoConfig,
    pointFigureConfig,
    periodicity,
    pendingInvalidation,
    visibleBars,
    resetTriggered,
    storedXExtents: resetTriggered ? null : storedXExtents,
    onVisibleBarsChange: handleVisibleBarsChange,
    // Compare mode
    comparisonSymbols,
    isCompareMode,
  });

  // Reset the resetTriggered flag after chart has updated
  React.useEffect(() => {
    if (resetTriggered) {
      setResetTriggered(false);
    }
  }, [chartState.xExtents, resetTriggered]);

  // Issue #105: Store chart state on unmount for auto-scroll on remount
  // Use a ref to capture the latest values for the cleanup function
  const chartStateRef = React.useRef({
    data: chartState.data,
    xExtents: chartState.xExtents,
  });
  React.useEffect(() => {
    chartStateRef.current = {
      data: chartState.data,
      xExtents: chartState.xExtents,
    };
  }, [chartState.data, chartState.xExtents]);

  // Store hidden state when chart unmounts (tab switch)
  React.useEffect(() => {
    return () => {
      const { data, xExtents } = chartStateRef.current;
      if (data?.length && xExtents) {
        const lastBarIndex = data.length - 1;
        const wasLastBarVisible = isLastBarVisibleInExtents(
          xExtents,
          lastBarIndex,
        );
        setChartHiddenState(chartId, lastBarIndex, wasLastBarVisible);
      }
    };
  }, [chartId, setChartHiddenState]);

  // Guards
  if (!chartId) return null;
  if (!periodicity) return null;
  // #432: a agregação não produziu nada apesar de haver barras cruas. Vem ANTES
  // dos guards de dado porque nesse caso `chartState.data` guarda o resultado
  // ANTERIOR (useChartData aborta o setState) — sem isto o gráfico velho fica na
  // tela, ou o spinner gira para sempre, e o usuário não sabe o que ajustar.
  if (chartState.aggregationEmpty) {
    return (
      <AggregationEmptyPanel
        ref={ref}
        chartType={chartType}
        chartId={chartId}
        chartData={initialData}
        periodicity={periodicity}
      />
    );
  }
  if ((chartState.data?.length ?? 0) === 0) {
    const spinnerColor = themeColors.text;
    const spinnerTrack = themeColors.border;

    return (
      <div
        ref={ref}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 180,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "0.75rem",
          color: themeColors.textSecondary,
          position: "relative",
          overflow: "hidden",
          background: themeColors.background,
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            border: `3px solid ${spinnerTrack}`,
            borderTopColor: spinnerColor,
            animation: "chart-loading-spin 0.8s linear infinite",
          }}
        />
        <style>{`
          @keyframes chart-loading-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  // ChartCanvas requires at least 2 datapoints to calculate scales
  // Aggregation chart types (Renko, P&F) may produce fewer bricks than source data
  if ((chartState.data?.length ?? 0) < 2) {
    if (AGGREGATION_TYPES.includes(chartType)) {
      return (
        <AggregationEmptyPanel
          ref={ref}
          chartType={chartType}
          chartId={chartId}
          chartData={initialData}
          periodicity={periodicity}
        />
      );
    }
    return null;
  }
  if (
    chartType === "pointfigure" &&
    (!chartState.data[0] || !chartState.data[0].boxes)
  )
    return null;

  // Safety check: ensure xExtents are valid for current data
  // This prevents "0 datapoints" errors when extents are out of range
  const { data, xAccessor, xExtents } = chartState;
  let safeXExtents = xExtents;
  if (data && data.length >= 2 && xAccessor) {
    const maxIndex = xAccessor(data[data.length - 1]);
    const minIndex = xAccessor(data[0]);

    // Calculate safe extents for current data
    const calculateSafeExtents = () => {
      const visibleBars = Math.min(100, data.length);
      const start = xAccessor(data[Math.max(0, data.length - visibleBars)]);
      const end = maxIndex;
      return [start, end];
    };

    // Force recalculation if:
    // 1. No xExtents at all
    // 2. Extents are completely out of range
    // 3. Extents span beyond data (e.g., [0, 500] when we only have 10 Renko bricks)
    // 4. Aggregation chart types (Renko, P&F) that may have drastically different data lengths
    // A right edge past the last bar is NOT a recalc trigger: a deliberate
    // right-pan (whitespace after the last bar) is a valid viewport state
    // (issues #168, #163). Only genuinely unusable extents reset here.
    const needsRecalc =
      !xExtents ||
      xExtents[0] > maxIndex ||
      xExtents[1] < minIndex ||
      (AGGREGATION_TYPES.includes(chartType) &&
        xExtents[1] - xExtents[0] > data.length * 2);

    if (needsRecalc) {
      safeXExtents = calculateSafeExtents();
    }
  }

  // Add chartId to chartState for interactive storage
  const enhancedChartState = { ...chartState, chartId, xExtents: safeXExtents };

  return (
    <div
      ref={ref}
      data-chart-xextents={
        safeXExtents ? `${safeXExtents[0]},${safeXExtents[1]}` : ""
      }
    >
      {/* Replay Warning Banner - Shows when replay mode is active */}
      <ReplayIndicator />

      {/* Debug Panel - Performance Monitor + Replay Controls (Ctrl+Shift+D) */}
      {/* Series Debug Panel - Shows all indicators with values (DEBUG:SERIES) */}
      {/* Feature flag: VITE_DEBUG_PANELS=true/false in .env */}
      {DEBUG_PANELS_ENABLED && (
        <Suspense fallback={null}>
          <DebugPanel />
          <SeriesDebugPanel />
        </Suspense>
      )}

      <ChartErrorBoundary>
        <ChartConfigProvider
          chartType={chartType}
          chartSymbol={chartSymbol}
          indicators={indicators}
          isCompareMode={isCompareMode}
          comparisonSymbols={comparisonSymbols}
        >
          {(configContext) => {
            const { allCharts } = configContext;

            // Build ordered charts (moved from ChartRenderer)
            const orderedCharts = React.useMemo(() => {
              // Get current section order or generate default if it doesn't exist or length mismatch
              let currentSectionOrder = sectionOrder;

              // If no section order exists, or if the number of charts changed, create/update the default order
              if (
                !currentSectionOrder ||
                currentSectionOrder.length !== allCharts.length
              ) {
                currentSectionOrder = getDefaultSectionOrder(chartId);
                // Update the store with the new default order to keep it in sync
                if (currentSectionOrder.length === allCharts.length) {
                  setSectionOrder(chartId, currentSectionOrder);
                }
              }

              // Apply section ordering
              if (
                currentSectionOrder &&
                currentSectionOrder.length === allCharts.length
              ) {
                // Update the index property to reflect new order
                return currentSectionOrder
                  .map((sectionIndex, newIndex) => {
                    const chart = allCharts[sectionIndex];
                    return { ...chart, index: newIndex };
                  })
                  .filter(Boolean);
              }

              return allCharts;
            }, [
              allCharts,
              sectionOrder,
              chartId,
              getDefaultSectionOrder,
              setSectionOrder,
            ]);

            // Compute initial chart configs (without resize heights first)
            const initialChartConfigs = useChartLayout(
              height,
              orderedCharts,
              null,
            );

            // Use the resize hook with initial chart configs
            const {
              tempSectionHeights: resizeTempHeights,
              handleStartResize,
              handleResize,
              handleEndResize,
            } = useChartResize(chartId, height, initialChartConfigs);

            // Get stored section heights from the chart store
            const storedSectionHeights = useChartsStore((state) =>
              state.getSectionHeights(chartId),
            );
            const setSectionHeights = useChartsStore(
              (state) => state.setSectionHeights,
            );

            // Redistribute ratios when new indicators are added
            const redistributedSectionHeights = React.useMemo(() => {
              if (!storedSectionHeights) {
                return storedSectionHeights;
              }

              // Check if there are new sections that don't have heights assigned
              // Only redistribute if there are actually new sections, not just reordered ones
              const hasNewSections = orderedCharts.some((chart) => {
                // Check if this chart index exists in storedSectionHeights
                return !(chart.index in storedSectionHeights);
              });

              // Additional check: if all charts have heights and the total sums to ~1.0, don't redistribute
              const allChartsHaveHeights = orderedCharts.every(
                (chart) => chart.index in storedSectionHeights,
              );
              const totalHeightSum = allChartsHaveHeights
                ? Object.values(storedSectionHeights).reduce(
                    (sum, height) => sum + height,
                    0,
                  )
                : 0;
              const isHeightSumValid = Math.abs(totalHeightSum - 1.0) < 0.01; // Allow small floating point differences

              if (!hasNewSections && allChartsHaveHeights && isHeightSumValid) {
                return storedSectionHeights;
              } // New indicators were added - redistribute ratios
              const newHeights = { ...storedSectionHeights };
              const existingRatioSum = Object.values(
                storedSectionHeights,
              ).reduce((sum, ratio) => sum + ratio, 0);

              if (existingRatioSum >= 1.0) {
                // Existing ratios take up all space, redistribute equally
                const equalRatio = 1.0 / orderedCharts.length;
                orderedCharts.forEach((chart) => {
                  newHeights[chart.index] = equalRatio;
                });
              } else {
                // There's remaining space, distribute it to new indicators
                const remainingRatio = 1.0 - existingRatioSum;
                const newIndicatorsCount = orderedCharts.filter(
                  (chart) => !storedSectionHeights[chart.index],
                ).length;
                const newIndicatorRatio = remainingRatio / newIndicatorsCount;

                // Set ratio for new indicators
                orderedCharts.forEach((chart) => {
                  if (!storedSectionHeights[chart.index]) {
                    newHeights[chart.index] = newIndicatorRatio;
                  }
                });
              }

              // Update the store with redistributed heights
              setSectionHeights(chartId, newHeights);
              return newHeights;
            }, [
              storedSectionHeights,
              orderedCharts,
              chartId,
              setSectionHeights,
            ]);

            // Use temp heights during resize, stored heights otherwise
            const sectionHeights =
              resizeTempHeights || redistributedSectionHeights;

            // Compute final chart configs with appropriate heights
            const chartConfigs = useChartLayout(
              height,
              orderedCharts,
              sectionHeights,
            );
            const layoutSnapshot = useChartLayoutSnapshot({
              chartId,
              width,
              height,
              ratio,
              sizingTelemetry: props.sizingTelemetry,
              chartConfigs,
            });

            return (
              <ChartRenderer
                chartId={chartId}
                width={width}
                height={height}
                ratio={ratio}
                type={type}
                chartState={enhancedChartState}
                chartConfigs={chartConfigs}
                layoutSnapshot={layoutSnapshot}
                orderedCharts={orderedCharts}
                chartSymbol={chartSymbol}
                periodicity={periodicity}
                showGrid={showGrid}
                indicatorMenuRef={indicatorMenuRef}
                chartMenuRef={chartMenuRef}
                handleInteractiveContextMenu={handleInteractiveContextMenu}
                handleOpenEditModal={handleOpenEditModal}
                handleMainInteractiveApiReady={handleMainInteractiveApiReady}
                handleInteractiveRemove={handleInteractiveRemove}
                handleRemoveIndicator={removeIndicatorFromChart}
                resetKey={resetKey}
                handleReset={handleReset}
                isPointerInsideChartPanel={isPointerInsideChartPanel}
                handleChartCanvasVisibleBarsChange={
                  handleChartCanvasVisibleBarsChange
                }
                handleChartCanvasXExtentsChange={
                  handleChartCanvasXExtentsChange
                }
                handleLoadMore={handleLoadMore}
                moveSectionUp={moveSectionUp}
                moveSectionDown={moveSectionDown}
                handleStartResize={handleStartResize}
                handleResize={handleResize}
                handleEndResize={handleEndResize}
                renkoConfigHash={renkoConfigHash}
                pointFigureConfigHash={pointFigureConfigHash}
                // Compare mode
                isCompareMode={isCompareMode}
                comparisonSymbols={comparisonSymbols}
                removeComparisonSymbol={removeComparisonSymbol}
                clearComparisons={clearComparisons}
              />
            );
          }}
        </ChartConfigProvider>

        <IndicatorContextMenu
          ref={indicatorMenuRef}
          chartId={chartId}
          onEditSave={handleIndicatorEditSave}
          onRemove={handleIndicatorRemove}
        />

        <InteractiveContextMenu
          ref={interactiveMenuRef}
          chartId={chartId}
          onDeleteInteractive={handleInteractiveRemove}
          onEditInteractive={handleInteractiveEdit}
        />

        {/* Unified chart context menu — right-click / long-press anywhere on
            the chart. Gated by the chartContextMenu feature flag (read at USE
            time); the legacy drawing/indicator menus above stay mounted and
            handle their edit modals — this menu only delegates to them. */}
        {isFeatureEnabled("chartContextMenu") && (
          <ChartContextMenu
            ref={chartMenuRef}
            chartId={chartId}
            interactiveMenuRef={interactiveMenuRef}
            indicatorMenuRef={indicatorMenuRef}
            onDeleteInteractive={handleInteractiveRemove}
            mainInteractiveApi={mainInteractiveApi}
          />
        )}
      </ChartErrorBoundary>
    </div>
  );
});

ChartWrapper.displayName = "ChartWrapper";

ChartWrapper.propTypes = {
  chartId: PropTypes.string,
  type: PropTypes.string,
  ratio: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  onInteractiveApiReady: PropTypes.func,
  isPointerInsideChartPanel: PropTypes.bool,
};

export default fitWidth(ChartWrapper, true);
